import React, { useState } from "react";
import { Box } from "@mui/joy";
import { theme } from "styles/theme";
import type { AssistantStep } from "./types";

function summarizeToolArgs(
  name: string,
  args: Record<string, unknown>
): string {
  if (typeof args.file_path === "string") {
    const parts = args.file_path.split("/");
    return parts[parts.length - 1] || args.file_path;
  }
  if (typeof args.path === "string") return args.path;
  return Object.keys(args).slice(0, 2).join(", ") || "";
}

export function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

// =========================
// ToolCallRow — line status with expandable result
// =========================

export function ToolCallRow({
  step,
}: {
  step: Extract<AssistantStep, { kind: "tool" }>;
}) {
  const [expanded, setExpanded] = useState(false);
  const running = step.status === "running";
  const argsSummary = summarizeToolArgs(step.name, step.args);
  const duration =
    step.finishedAt && step.startedAt
      ? formatDuration(step.finishedAt - step.startedAt)
      : null;
  const hasResult = !!step.result;

  return (
    <Box
      onClick={() => hasResult && setExpanded((v) => !v)}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "2px",
        px: "8px",
        py: "5px",
        borderRadius: "6px",
        cursor: hasResult ? "pointer" : "default",
        transition: "background-color 0.15s",
        "&:hover": hasResult
          ? { backgroundColor: theme.vars.palette.Background.Level2 }
          : {},
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          fontFamily:
            "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
          fontSize: "12px",
        }}
      >
        <Box
          sx={{
            width: "12px",
            height: "12px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: running ? theme.vars.palette.primary.solid : "#15803d",
            flexShrink: 0,
          }}
        >
          {running ? (
            <Box
              sx={{
                width: "10px",
                height: "10px",
                borderRadius: "50%",
                border: `1.5px solid ${theme.vars.palette.Background.Level3 ?? "#ddd"}`,
                borderTopColor: theme.vars.palette.primary.solid,
                animation: "spin 0.8s linear infinite",
                "@keyframes spin": {
                  to: { transform: "rotate(360deg)" },
                },
              }}
            />
          ) : (
            <Box
              component="span"
              sx={{ fontSize: "12px", lineHeight: 1, fontWeight: 700 }}
            >
              ✓
            </Box>
          )}
        </Box>
        <Box
          component="span"
          sx={{
            color: theme.vars.palette.Content.Strong,
            fontWeight: 600,
          }}
        >
          {step.name}
        </Box>
        <Box
          component="span"
          sx={{
            color: theme.vars.palette.Content.Weak,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
            minWidth: 0,
          }}
        >
          {argsSummary}
        </Box>
        {duration && (
          <Box
            component="span"
            sx={{
              fontSize: "10px",
              color: theme.vars.palette.Content.Weak,
              flexShrink: 0,
            }}
          >
            {duration}
          </Box>
        )}
        {hasResult && (
          <Box
            component="span"
            sx={{
              fontSize: "10px",
              color: theme.vars.palette.Content.Weak,
              flexShrink: 0,
            }}
          >
            {expanded ? "▾" : "▸"}
          </Box>
        )}
      </Box>
      {expanded && step.result && (
        <Box
          sx={{
            mt: "6px",
            ml: "20px",
            p: "8px 10px",
            borderRadius: "4px",
            backgroundColor: theme.vars.palette.Background.Level2,
            border: `1px solid ${theme.vars.palette.Border.Weak}`,
            fontFamily:
              "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
            fontSize: "11px",
            lineHeight: 1.5,
            color: theme.vars.palette.Content.Medium,
            whiteSpace: "pre-wrap",
            wordBreak: "break-all",
            maxHeight: "200px",
            overflow: "auto",
          }}
        >
          {step.result}
        </Box>
      )}
    </Box>
  );
}
